import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, FileCheck, Plane, BedDouble, Mountain, Car, Globe2 } from "lucide-react";
import { waLink } from "@/lib/api";
import { usePageMeta } from "@/lib/seo";

const groups = [
  {
    title: "UAE Visa Services",
    icon: FileCheck,
    items: [
      "UAE Tourist Visas",
      "30 Days Single Entry Visa",
      "60 Days Single Entry Visa",
      "A2A Visa Change",
      "Oman Visa Change",
      "Inside-country Visa Extension",
    ],
    cta: { to: "/visa", label: "Apply for Visa" },
  },
  {
    title: "Flights & Stays",
    icon: Plane,
    items: ["Flight Tickets (One-way / Return)", "Hotel Bookings", "Airport Transfers"],
    cta: { to: "/flights", label: "Request a Flight Quote" },
  },
  {
    title: "Holidays & Experiences",
    icon: Mountain,
    items: ["Custom Holiday Packages", "Tours & Excursions", "Desert Safaris & City Tours"],
    cta: { to: "/holidays", label: "Plan a Holiday" },
  },
  {
    title: "Business & Consultation",
    icon: Globe2,
    items: ["Group Travel", "Business Travel Support", "Travel Consultation", "Airport Transfers"],
    cta: { to: "/corporate", label: "Corporate Travel Enquiry" },
  },
];

export default function Services() {
  usePageMeta("Our Services", "Flights, UAE visas, hotels, holiday packages, corporate travel, travel insurance and airport transfers — full-service travel solutions worldwide.");
  return (
    <div className="bg-white">
      <section className="bg-[#0A2E4D] text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <span className="mk-gold-line mb-6 block" />
          <p className="text-xs uppercase tracking-[0.25em] text-[#D4A62A] font-bold mb-3">Our Services</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl">A complete travel desk.</h1>
          <p className="text-slate-300 mt-5 max-w-3xl text-base">Complete travel solutions from an international travel company registered in the United Arab Emirates, Zimbabwe and South Africa — from UAE visas to worldwide holiday packages, we help you travel smoothly from enquiry to arrival.</p>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-8">
          {groups.map((g) => (
            <div key={g.title} className="mk-card p-8 rounded-md flex flex-col">
              <g.icon size={36} className="text-[#D4A62A]" strokeWidth={1.5} />
              <h3 className="font-serif-display text-2xl text-[#0A2E4D] mt-5">{g.title}</h3>
              <ul className="mt-6 space-y-2.5 text-sm text-[#475569] flex-1">
                {g.items.map((it) => (
                  <li key={it} className="flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#D4A62A] mt-2 shrink-0" />{it}
                  </li>
                ))}
              </ul>
              <Link to={g.cta.to} className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-[#0A2E4D] border-b-2 border-[#D4A62A] pb-1 self-start hover:gap-3 transition-all"
                    data-testid={`services-cta-${g.cta.to.slice(1)}`}>
                {g.cta.label} <ArrowRight size={14} />
              </Link>
            </div>
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-6 mt-16">
          <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <h3 className="font-serif-display text-2xl text-[#0A2E4D]">Need something custom?</h3>
              <p className="text-sm text-[#475569] mt-2">Tell us your plan and we&apos;ll tailor the perfect itinerary.</p>
            </div>
            <a href={waLink("Hello Makchester Tours & Travel, I would like a custom travel plan.")} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white px-7 py-3.5 rounded-full text-sm font-semibold transition-colors" data-testid="services-custom-whatsapp">
              <MessageCircle size={16} /> WhatsApp a Travel Expert
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
